import {
  AgentCodeTestConfigChange,
  getAgentTestChangeRowId,
} from '@elementary/common-ui/types';
import { deriveParamsFromYaml } from '@elementary/common-ui/utils/codeTestConfig';
import { AddTestForm } from 'shared/types';
import { CodePlanWizardState } from 'shared/components/AddTestWizard/types';
import {
  Mapper,
  DirtyFields,
  PlanFieldDescriptor,
  changeToForm,
  extractNew,
  formToChange,
  isPathDirty,
  listMapper,
  makeSeverityMapper,
  nullableMapper,
  setFieldNew,
} from './planFieldMappers';

const whereMapper: Mapper = {
  toForm: (planValue) => (planValue as string | null) ?? '',
  toPlan: (formValue) => {
    const trimmed = typeof formValue === 'string' ? formValue.trim() : '';
    return trimmed ? trimmed : null;
  },
};

const limitMapper: Mapper = {
  toForm: (planValue) => (planValue as number | null) ?? undefined,
  toPlan: (formValue) => {
    if (formValue === undefined || formValue === null || formValue === '')
      return null;
    const parsed = Number(formValue);
    if (Number.isNaN(parsed) || parsed === 0) return null;
    return parsed;
  },
};

const storeFailuresMapper: Mapper = {
  toForm: (planValue) => (planValue as boolean | undefined) ?? undefined,
  toPlan: (formValue) => (formValue as boolean | undefined) ?? false,
};

const CODE_DESCRIPTORS: PlanFieldDescriptor[] = [
  {
    planKey: 'severity',
    formPath: 'severity',
    mapper: makeSeverityMapper('lower'),
  },
  { planKey: 'owners', formPath: 'owners', mapper: listMapper },
  { planKey: 'tags', formPath: 'tags', mapper: listMapper },
  { planKey: 'description', formPath: 'description', mapper: nullableMapper },
  {
    planKey: 'where',
    targetPrefix: 'params',
    formPath: 'whereClause',
    mapper: whereMapper,
  },
  {
    planKey: 'limit',
    targetPrefix: 'params',
    formPath: 'limit',
    mapper: limitMapper,
  },
  {
    planKey: 'store_failures',
    targetPrefix: 'params',
    formPath: 'shouldStoreFailures',
    mapper: storeFailuresMapper,
  },
];

export const buildCodePlanWizardState = (params: {
  change: AgentCodeTestConfigChange;
  operation: 'create' | 'update';
  assetId: string;
}): CodePlanWizardState => {
  // Config args (where/limit/store_failures) can be carried in the yaml edit
  // rather than explicit params; fold them into params so the Advanced fields
  // seed from the yaml too (explicit params take precedence on conflict).
  const effectiveChange: AgentCodeTestConfigChange = {
    ...params.change,
    params: {
      ...deriveParamsFromYaml(params.change.yaml),
      ...(params.change.params ?? {}),
    },
  };
  return {
    planSource: 'code-plan',
    planRowId: getAgentTestChangeRowId(params.change),
    operation: params.operation,
    wizardResourceType: params.change.column ? 'columns' : 'tables',
    initialValues: changeToForm(effectiveChange, CODE_DESCRIPTORS, {
      resourceType: params.change.column ? 'columns' : 'tables',
      resources: [params.assetId],
      testType: 'yamlTest',
      yaml: extractNew(params.change.yaml) ?? '',
    }),
  };
};

export const applyFormToCodeChange = (
  change: AgentCodeTestConfigChange,
  form: AddTestForm,
  dirtyFields: DirtyFields,
): AgentCodeTestConfigChange => {
  const next = formToChange(change, form, dirtyFields, CODE_DESCRIPTORS);

  // YAML isn't a descriptor: the wizard editor is seeded with the YAML the
  // preview-enrichment step put on the change (`change.yaml.new`), so we only
  // write the editor's value back when the user actually edited it — and never
  // let a blank editor wipe that enriched YAML.
  const editorYaml = form.yaml ?? '';
  const enrichedYaml = change.yaml?.new?.trim() ?? '';

  const isYamlEdited = isPathDirty(dirtyFields, 'yaml');
  const isEditorYamlEmpty = editorYaml.trim() === '';
  const wouldWipeEnrichedYaml = isEditorYamlEmpty && enrichedYaml !== '';

  if (isYamlEdited && !wouldWipeEnrichedYaml) {
    return { ...next, yaml: setFieldNew(change.yaml, editorYaml) };
  }
  return next;
};
